import React, { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import * as styles from './Modal.module.scss';
import {
  Modal,
  ModalHeader,
  ModalFooter,
  ModalBody,
  Text,
  Link,
  Button,
} from 'components/core';
import { DataExtractionTemplate as DataExtractionTemplateType } from 'types/DataExtraction';

export function changedDefaultInterventions(
  form: UseFormReturn<DataExtractionTemplateType, any, undefined>
): boolean {
  const { getValues, getFieldState } = form;
  return (
    publishedTemplate(form) ||
    getValues('interventions.interventions').length !== 2 ||
    getFieldState('interventions.interventions.0.name').isDirty ||
    getFieldState('interventions.interventions.1.name').isDirty
  );
}

export function changedDefaultOutcomes(
  form: UseFormReturn<DataExtractionTemplateType, any, undefined>
): boolean {
  const { getValues, getFieldState } = form;
  const hasExampleQualitativeOutcome =
    getValues('outcomes.outcomes.2.name') === 'Example qualitative outcome'; // This is only present for selectable measures reviews
  const numberOfOutcomes = getValues('outcomes.outcomes').length;

  const numberOfExampleOutcomes = hasExampleQualitativeOutcome ? 3 : 2;
  const outcomesCountMismatch = numberOfOutcomes !== numberOfExampleOutcomes;

  // There are up to three example outcomes
  const outcomeNamesDirty = [0, 1, 2].some(
    (index) => getFieldState(`outcomes.outcomes.${index}.name`).isDirty
  );
  return publishedTemplate(form) || outcomesCountMismatch || outcomeNamesDirty;
}

function publishedTemplate(
  form: UseFormReturn<DataExtractionTemplateType, any, undefined>
): boolean {
  const { getValues } = form;
  return !!getValues('updated_by');
}
export function showUnchangedDefaultWarning(
  form: UseFormReturn<DataExtractionTemplateType, any, undefined>
) {
  // Currently we only care if the user has change the default
  // interventions or outcomes so we only check these.
  return !(changedDefaultInterventions(form) || changedDefaultOutcomes(form));
}

type Props = {
  onClose: () => void;
  onPublish: (revisit?: boolean) => void;
};

export const UnchangedDefaultsWarningModal = ({
  onClose,
  onPublish,
}: Props) => {
  const [isOpen, setOpen] = useState(true);

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        setOpen(false);
        onClose();
      }}
    >
      <ModalHeader>Are you sure you want to publish?</ModalHeader>
      <ModalBody>
        <Text className={styles.ModalBodyText}>
          You haven&apos;t made many changes to the default template.
        </Text>
        <Text className={styles.ModalBodyText}>
          <strong>
            We strongly recommend that you edit the default list of
            interventions and outcomes
          </strong>{' '}
          so that you can collect data that&apos;s relevant for your review.
        </Text>
        <Text>
          Learn how to set up your template using this{' '}
          <Link
            to={
              'https://support.covidence.org/help/how-to-create-a-data-extraction-template-in-extraction-1'
            }
            target="_blank"
            variant="primary"
          >
            step-by-step guide
          </Link>
          .
        </Text>
      </ModalBody>
      <ModalFooter>
        <Button
          type="neutral"
          variant="outline"
          onClick={() => {
            onClose();
            onPublish();
          }}
        >
          Publish template
        </Button>
        <Button type="brand" onClick={onClose}>
          Continue editing
        </Button>
      </ModalFooter>
    </Modal>
  );
};
